/**
 * Notifications Page
 *
 * Full page view for all notifications with filtering, pagination, and bulk actions.
 */

import React, { useState, useEffect, useCallback } from 'react';
import {
    Box,
    Typography,
    Paper,
    Tabs,
    Tab,
    Button,
    CircularProgress,
    Pagination,
    FormControlLabel,
    Switch,
    Divider,
    IconButton,
    Tooltip,
    alpha,
} from '@mui/material';
import {
    NotificationsOff as NotificationsOffIcon,
    DoneAll as DoneAllIcon,
    Refresh as RefreshIcon,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { notificationsApi } from '../../services/api';
import NotificationItem from '../../components/notifications/NotificationItem';

const CATEGORIES = [
    { value: null, label: 'All' },
    { value: 'PAYMENT', label: 'Payments' },
    { value: 'LEASE', label: 'Leases' },
    { value: 'MAINTENANCE', label: 'Maintenance' },
    { value: 'TENANT', label: 'Tenants' },
    { value: 'SYSTEM', label: 'System' },
];

const PAGE_SIZE = 20;

const Notifications = () => {
    const navigate = useNavigate();

    const [notifications, setNotifications] = useState([]);
    const [loading, setLoading] = useState(true);
    const [selectedCategory, setSelectedCategory] = useState(null);
    const [showUnreadOnly, setShowUnreadOnly] = useState(false);
    const [currentPage, setCurrentPage] = useState(1);
    const [totalCount, setTotalCount] = useState(0);
    const [unreadCount, setUnreadCount] = useState(0);

    const totalPages = Math.ceil(totalCount / PAGE_SIZE);

    const fetchNotifications = useCallback(async () => {
        setLoading(true);
        try {
            const params = {
                page: currentPage,
                page_size: PAGE_SIZE,
            };
            if (selectedCategory) params.category = selectedCategory;
            if (showUnreadOnly) params.is_read = false;

            const response = await notificationsApi.getNotifications(params);
            if (response.ok && response.data) {
                setNotifications(response.data.notifications || []);
                setTotalCount(response.data.total || 0);
                setUnreadCount(response.data.unread_count || 0);
            }
        } catch (err) {
            console.error('Failed to fetch notifications:', err);
        } finally {
            setLoading(false);
        }
    }, [currentPage, selectedCategory, showUnreadOnly]);

    useEffect(() => {
        fetchNotifications();
    }, [fetchNotifications]);

    const handleCategoryChange = (event, newValue) => {
        setSelectedCategory(newValue);
        setCurrentPage(1);
    };

    const handlePageChange = (event, page) => {
        setCurrentPage(page);
    };

    const handleMarkAsRead = async (notificationId) => {
        try {
            const response = await notificationsApi.markAsRead(notificationId);
            if (response.ok) {
                setNotifications(prev =>
                    prev.map(n =>
                        n.notification_id === notificationId
                            ? { ...n, is_read: true, read_at: new Date().toISOString() }
                            : n
                    )
                );
                setUnreadCount(prev => Math.max(0, prev - 1));
            }
        } catch (err) {
            console.error('Failed to mark as read:', err);
        }
    };

    const handleMarkAllAsRead = async () => {
        try {
            const response = await notificationsApi.markAllAsRead(selectedCategory);
            if (response.ok) {
                setNotifications(prev =>
                    prev.map(n =>
                        (!selectedCategory || n.category === selectedCategory)
                            ? { ...n, is_read: true, read_at: new Date().toISOString() }
                            : n
                    )
                );
                fetchNotifications();
            }
        } catch (err) {
            console.error('Failed to mark all as read:', err);
        }
    };

    const handleDelete = async (notificationId) => {
        try {
            const response = await notificationsApi.deleteNotification(notificationId);
            if (response.ok) {
                const deletedNotification = notifications.find(n => n.notification_id === notificationId);
                setNotifications(prev => prev.filter(n => n.notification_id !== notificationId));
                if (deletedNotification && !deletedNotification.is_read) {
                    setUnreadCount(prev => Math.max(0, prev - 1));
                }
                setTotalCount(prev => Math.max(0, prev - 1));
            }
        } catch (err) {
            console.error('Failed to delete notification:', err);
        }
    };

    const handleNotificationClick = (notification, actionUrl) => {
        if (actionUrl) {
            navigate(actionUrl);
        }
    };

    return (
        <Box sx={{ maxWidth: 900, mx: 'auto', py: 3 }}>
            {/* Header */}
            <Box
                sx={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    mb: 3,
                }}
            >
                <Box>
                    <Typography variant="h5" sx={{ fontWeight: 600, color: '#1a1a2e' }}>
                        Notifications
                    </Typography>
                    <Typography variant="body2" sx={{ color: '#6b7280', mt: 0.5 }}>
                        {unreadCount > 0
                            ? `You have ${unreadCount} unread notification${unreadCount !== 1 ? 's' : ''}`
                            : 'All caught up!'}
                    </Typography>
                </Box>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Tooltip title="Refresh">
                        <IconButton onClick={fetchNotifications} disabled={loading}>
                            <RefreshIcon />
                        </IconButton>
                    </Tooltip>
                    {unreadCount > 0 && (
                        <Button
                            variant="outlined"
                            startIcon={<DoneAllIcon />}
                            onClick={handleMarkAllAsRead}
                            sx={{ textTransform: 'none' }}
                        >
                            Mark all as read
                        </Button>
                    )}
                </Box>
            </Box>

            {/* Main Content */}
            <Paper
                elevation={0}
                sx={{
                    borderRadius: 2,
                    border: '1px solid',
                    borderColor: 'divider',
                    overflow: 'hidden',
                }}
            >
                {/* Filters */}
                <Box
                    sx={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                        px: 2,
                        py: 1,
                        borderBottom: '1px solid',
                        borderColor: 'divider',
                        bgcolor: alpha('#000', 0.01),
                    }}
                >
                    <Tabs
                        value={selectedCategory}
                        onChange={handleCategoryChange}
                        variant="scrollable"
                        scrollButtons="auto"
                        sx={{
                            minHeight: 40,
                            '& .MuiTab-root': {
                                minHeight: 40,
                                py: 0,
                                px: 2,
                                fontSize: '0.875rem',
                                textTransform: 'none',
                            },
                        }}
                    >
                        {CATEGORIES.map((cat) => (
                            <Tab key={cat.label} value={cat.value} label={cat.label} />
                        ))}
                    </Tabs>
                    <FormControlLabel
                        control={
                            <Switch
                                checked={showUnreadOnly}
                                onChange={(e) => {
                                    setShowUnreadOnly(e.target.checked);
                                    setCurrentPage(1);
                                }}
                                size="small"
                            />
                        }
                        label={
                            <Typography variant="body2" sx={{ color: '#6b7280' }}>
                                Unread only
                            </Typography>
                        }
                        sx={{ mr: 0 }}
                    />
                </Box>

                {/* Notification List */}
                <Box sx={{ minHeight: 400 }}>
                    {loading ? (
                        <Box
                            sx={{
                                display: 'flex',
                                justifyContent: 'center',
                                alignItems: 'center',
                                py: 8,
                            }}
                        >
                            <CircularProgress />
                        </Box>
                    ) : notifications.length === 0 ? (
                        <Box
                            sx={{
                                display: 'flex',
                                flexDirection: 'column',
                                alignItems: 'center',
                                justifyContent: 'center',
                                py: 8,
                                color: '#9ca3af',
                            }}
                        >
                            <NotificationsOffIcon sx={{ fontSize: 64, mb: 2 }} />
                            <Typography variant="h6" sx={{ mb: 1 }}>
                                No notifications
                            </Typography>
                            <Typography variant="body2">
                                {showUnreadOnly
                                    ? "You've read all your notifications"
                                    : selectedCategory
                                    ? `No ${selectedCategory.toLowerCase()} notifications yet`
                                    : "You don't have any notifications yet"}
                            </Typography>
                        </Box>
                    ) : (
                        notifications.map((notification) => (
                            <NotificationItem
                                key={notification.notification_id}
                                notification={notification}
                                onMarkAsRead={handleMarkAsRead}
                                onDelete={handleDelete}
                                onClick={handleNotificationClick}
                            />
                        ))
                    )}
                </Box>

                {/* Pagination */}
                {totalPages > 1 && (
                    <>
                        <Divider />
                        <Box
                            sx={{
                                display: 'flex',
                                justifyContent: 'space-between',
                                alignItems: 'center',
                                px: 2,
                                py: 1.5,
                            }}
                        >
                            <Typography variant="body2" sx={{ color: '#6b7280' }}>
                                Showing {((currentPage - 1) * PAGE_SIZE) + 1} -{' '}
                                {Math.min(currentPage * PAGE_SIZE, totalCount)} of {totalCount}
                            </Typography>
                            <Pagination
                                count={totalPages}
                                page={currentPage}
                                onChange={handlePageChange}
                                color="primary"
                                size="small"
                            />
                        </Box>
                    </>
                )}
            </Paper>
        </Box>
    );
};

export default Notifications;
